import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Subscription, SubscriptionDelivery } from '@urc-ui/store/api/subscriptions/types';
import type { RootState } from '@urc-ui/store/types';
import { buildZonedTimeSubscriptionDelivery } from '@urc-ui/store/api/subscriptions/subscriptions';

export interface FormErrorStateType {
  readonly reportNameError: boolean;
  readonly reportDeliveryError: boolean;
}

interface ReportSettingsState {
  readonly reportName: string;
  readonly subscriptionDelivery: SubscriptionDelivery;
  readonly status: string;
  readonly recipients: string[];
  readonly isModified: boolean;
  readonly formErrorState: FormErrorStateType;
}

const initialState: ReportSettingsState = {
  reportName: '',
  subscriptionDelivery: {} as SubscriptionDelivery,
  status: '',
  recipients: [],
  isModified: false,
  formErrorState: { reportNameError: false, reportDeliveryError: false }
};

export interface SubscriptionData {
  subscription?: Subscription;
}

const EditReportSettingsSlice = createSlice({
  name: 'EditReportSettingsSlice',
  initialState,
  reducers: {
    setReportName: (state, action: PayloadAction<{ updatedReportName: string; reportNameError?: boolean }>) => {
      const { updatedReportName, reportNameError } = action.payload;
      state.reportName = updatedReportName;
      state.isModified = true;
      state.formErrorState.reportNameError = !!reportNameError;
    },
    setSubscriptionDelivery: (
      state,
      action: PayloadAction<{ subscriptionDelivery: SubscriptionDelivery; reportDeliveryError?: boolean }>
    ) => {
      const { subscriptionDelivery, reportDeliveryError } = action.payload;
      state.subscriptionDelivery = subscriptionDelivery;
      state.isModified = true;
      state.formErrorState.reportDeliveryError = !!reportDeliveryError;
    },
    setRecipients: (state, action: PayloadAction<string[]>) => {
      state.recipients = action.payload;
      state.isModified = true;
    },
    setIsModified: state => {
      state.isModified = true;
    },
    setSubscriptionSettings: (state, action: PayloadAction<SubscriptionData & { timezone: string }>) => {
      if (!action.payload.subscription) {
        return;
      }

      const { name: reportName, subscriptionDelivery, recipients, status } = action.payload.subscription;
      state.reportName = reportName;

      state.subscriptionDelivery = buildZonedTimeSubscriptionDelivery(subscriptionDelivery, action.payload.timezone);
      state.status = status;
      state.recipients = recipients;
      state.isModified = false;
    }
  }
});

const selectReportName = (state: RootState) => state.editReportSettings.reportName;
const selectSubscriptionDelivery = (state: RootState) => state.editReportSettings.subscriptionDelivery;
const selectStatus = (state: RootState) => state.editReportSettings.status;
const selectRecipients = (state: RootState) => state.editReportSettings.recipients;
const selectIsModified = (state: RootState) => state.editReportSettings.isModified;
export const getFormErrorState = (state: RootState) => state.editReportSettings.formErrorState;

export const { setReportName, setSubscriptionDelivery, setRecipients, setIsModified, setSubscriptionSettings } =
  EditReportSettingsSlice.actions;
export { selectReportName, selectSubscriptionDelivery, selectRecipients, selectIsModified, selectStatus };
export default EditReportSettingsSlice.reducer;
export type { ReportSettingsState };
